import { useEffect, useState, useCallback, useMemo } from "react";
import { STATUS_LABEL } from "@/components/TaskBadges";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { uploadFile, downloadSigned } from "@/lib/api";
import { Avatar } from "@/components/shared/Avatar";
import { useSubmitGuard } from "@/hooks/useSubmitGuard";
import { useAuth } from "@/context/AuthContext";
import { formatCZK, formatCZKCompact, formatHours, formatDate, formatDateTime, formatTime, currencySymbol, plur } from "@/lib/format";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { StatCard, TabSwitcher } from "@/components/shared/PagePrimitives";
import { FormSection } from "@/components/shared/FormSection";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AttendanceMonthlyView } from "@/components/AttendanceMonthlyView";
import { ArrowLeft, Clock, Wallet, ClipboardList, AlertTriangle, Phone, Mail, MapPin, Coins, ShieldAlert,
  Pencil, Smartphone, LifeBuoy, StickyNote, CircleDot, ChevronDown, ChevronRight,
  FileText, Plus, Trash2, Upload, CheckCircle2, User, Briefcase, Package, Camera } from "lucide-react";
import { toast } from "sonner";
import { ask } from "@/lib/confirm";
import { ContractPicker, contractOf, laborTerms } from "@/components/team/ContractPicker";

const ROLE_LABELS = { OWNER: "Majitel", MANAGER: "Stavbyvedoucí", WORKER: "Dělník", DESIGNER: "Projektant",
  SUBCONTRACTOR: "Subdodavatel", ASSISTANT: "Asistentka", ACCOUNTANT: "Účetní" };
// Role, u kterých má smysl profese na stavbě (stejné pravidlo jako v pozvánce).
const ROLES_WITH_POSITION = ["WORKER", "SUBCONTRACTOR"];
const MODE_LABELS = { site: "Na stavbě", daysheet: "Denní výkaz" };
const DOT = { shift: "bg-brand", task: "bg-success", diary: "bg-amber-500", doc: "bg-destructive", pay: "bg-muted-foreground", account: "bg-sky-500" };
const fmtH = (h) => Number(h).toLocaleString("cs-CZ", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const DONE_ST = ["Hotovo", "Schváleno"];
const DOC_TYPES = ["Školení BOZP", "Vazačský průkaz", "Jeřábnický průkaz", "Řidičský průkaz",
  "Lékařská prohlídka", "Svářečský průkaz", "Jiné"];
const todayISO = () => new Date().toISOString().slice(0, 10);
const OWNER_WARN = "Majitel má plná práva: sazby, mzdové náklady, ekonomika, fakturace i správa týmu. Povýšení potvrdíte uložením.";

export default function TeamMemberDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, company } = useAuth();
  const isOwner = user?.role === "OWNER";
  // Sazby a mzdové náklady vidí majitel a asistent/ka; měnit je smí jen majitel.
  const canSeeRates = isOwner || user?.role === "ASSISTANT";
  const [m, setM] = useState(null);
  // 179: stavbyvedoucí vidí a mění sazbu dělníka (ne vedení, ne nastavení účtu).
  const canPay = canSeeRates || (user?.role === "MANAGER" && m?.role === "WORKER");
  const [tab, setTab] = useState("overview");
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({});
  const [rateOpen, setRateOpen] = useState(false);
  const [rateBusy, guardRate] = useSubmitGuard();   // dvojklik nesmí zapsat sazbu dvakrát
  const [rateForm, setRateForm] = useState({ rate: "", valid_from: todayISO(), contract: null });
  const [attOpen, setAttOpen] = useState(false);
  const [attForm, setAttForm] = useState({ mode: "inherit", target: "" });
  const [confirm, setConfirm] = useState(null); // 'deactivate' | 'reactivate'
  const [delOpen, setDelOpen] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [expiredCount, setExpiredCount] = useState(0);
  const [activity, setActivity] = useState(null);
  const [actFilter, setActFilter] = useState("all");
  const [cov, setCov] = useState(null);   // pokrytí týmu (169/C2)

  // Pokrytí zakázek pro tohoto člena — jen vedení (endpoint je OWNER/ASSISTANT/MANAGER).
  const loadCov = useCallback(() => {
    // 219: karta člověka „docházku mimo tým“ nezobrazuje — bez skenu docházky všech zakázek
    api.get("/projects/team-coverage", { params: { attendance: false } }).then((r) => setCov(r.data)).catch(() => setCov(null));
  }, []);
  useEffect(() => { loadCov(); }, [loadCov, id]);
  const covMember = (cov?.members || []).find((x) => x.id === id);
  const activeProjCount = cov?.summary?.projects || 0;
  const onProjects = covMember ? (cov.projects || []).filter((p) => !(p.missing || []).some((mm) => mm.id === id)) : [];
  const missingProjCount = covMember ? (covMember.missing_project_ids || []).length : 0;

  const addToAllProjects = async () => {
    if (!await ask(`Přidat ${m?.name} na všechny aktivní zakázky? Chybí na ${missingProjCount} z ${activeProjCount}.`)) return;
    try {
      const { data } = await api.post("/projects/team/bulk", {
        mode: "add", project_ids: "all_active", user_ids: [id], dry_run: false });
      toast.success(`Přidáno na ${data.pairs} zakázek.`);
      loadCov();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    if (tab === "overview" && activity === null) {
      api.get(`/team/${id}/activity`).then((r) => setActivity(r.data)).catch(() => setActivity([]));
    }
  }, [tab, id, activity]);

  // 260: výjimka z blokace příchodu při nedoplněném dni (konec směny / denní výkaz)
  const saveNeverBlock = async (v) => {
    try { await api.patch(`/team/${id}`, { attendance_never_block: v }); toast.success(v ? "Do práce se přihlásí i bez doplnění." : "Platí nastavení firmy."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveReportMaterials = async (v) => {
    try { await api.patch(`/team/${id}`, { report_materials: v }); toast.success("Uloženo."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const load = useCallback(() => api.get(`/team/${id}`).then((r) => setM(r.data)).catch((e) => {
    toast.error(formatApiError(e.response?.data?.detail) || "Nepodařilo se načíst.");
    nav("/tym");
  }), [id, nav]);
  useEffect(() => { load(); }, [load]);

  // Počet propadlých dokumentů pro odznak u záložky + kartu „Blokuje práci".
  useEffect(() => {
    api.get(`/team/${id}/documents`).then((r) => setExpiredCount((r.data || []).filter((d) => d.expiry_status === "expired").length)).catch(() => {});
  }, [id]);
  // Když se po změně dat/role ztratí vybraná záložka, spadnout na Přehled.
  useEffect(() => {
    const allowed = ["overview", "attendance", "tasks", "docs", ...(canPay ? ["pay"] : []), ...(canSeeRates ? ["settings"] : [])];
    if (m && !allowed.includes(tab)) setTab("overview");
  }, [tab, canSeeRates, canPay, m]);

  // Roli smí měnit majitel (i druhému majiteli; server hlídá posledního majitele) a od 407 i asistent/ka —
  // ta ne majiteli a nikoho na majitele nepovýší. Vlastní roli nikdo.
  const canEditRole = m?.id !== user?.id && (isOwner || (user?.role === "ASSISTANT" && m?.role !== "OWNER"));

  // Deaktivaci smí majitel, asistentka i stavbyvedoucí (ten jen u dělníků
  // a subdodavatelů). Majitele smí deaktivovat jen jiný majitel. Trvalé smazání
  // zůstává výhradně majiteli. Poslední zůstávajícího majitele hlídá server.
  const canDeactivate = !!m && m.id !== user?.id && (
    m.role === "OWNER"
      ? isOwner
      : (isOwner || user?.role === "ASSISTANT" ||
         (user?.role === "MANAGER" && (m.role === "WORKER" || m.role === "SUBCONTRACTOR"))));

  const openEdit = () => {
    setForm({ name: m.name || "", email: m.email || "", position: m.position || "", phone: m.phone || "", mobile: m.mobile || "",
      address: m.address || "", emergency_contact_name: m.emergency_contact_name || "",
      emergency_contact_phone: m.emergency_contact_phone || "", internal_note: m.internal_note || "",
      role: m.role || "WORKER" });
    setEditOpen(true);
  };
  // 184: přihlašovací e-mail aktivního člena smí změnit majitel / asistentka
  // (majitelův e-mail jen majitel) — docházka, úkoly a historie zůstávají.
  const canEditEmail = m?.status === "invited" || (m?.status === "active" && canSeeRates && (m?.role !== "OWNER" || isOwner));
  const emailChanged = m?.status === "active" && (form.email || "").trim().toLowerCase() !== (m?.email || "").toLowerCase();
  const saveEdit = async () => {
    try {
      const payload = canEditEmail ? form : (({ email, ...rest }) => rest)(form);
      await api.patch(`/team/${id}`, payload);
      toast.success(emailChanged ? `E-mail změněn. ${m.name} se teď přihlásí novým e-mailem (přijde mu kód).` : "Uloženo.");
      setEditOpen(false); load(); setActivity(null);      // 407: změna role / e-mailu hned v „Co se dělo“
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveRate = async () => {
    if (rateForm.rate === "" || Number(rateForm.rate) < 0) return toast.error("Zadejte platnou sazbu.");
    try {
      // 281: typ spolupráce posíláme jen, když ho vedení vidí (stavbyvedoucí mění jen sazbu)
      await api.post(`/team/${id}/rate`, { rate: Number(rateForm.rate), valid_from: rateForm.valid_from || null,
        ...(canSeeRates && rateForm.contract ? { contract: rateForm.contract } : {}) });
      toast.success("Sazba změněna."); setRateOpen(false); setRateForm({ rate: "", valid_from: todayISO(), contract: null }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openAtt = () => {
    setAttForm({ mode: m.attendance_mode || "inherit", target: m.daily_target_hours != null ? String(m.daily_target_hours) : "" });
    setAttOpen(true);
  };
  const saveAtt = async () => {
    const payload = { attendance_mode: attForm.mode };
    const t = attForm.target.trim();
    if (t !== "") {
      const n = Number(t.replace(",", "."));
      if (isNaN(n) || n <= 0) return toast.error("Zadejte platný denní úvazek.");
      payload.daily_target_hours = n;
    } else {
      payload.daily_target_hours = null;
    }
    try {
      await api.patch(`/team/${id}`, payload);
      toast.success("Docházkový režim uložen."); setAttOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDeactivate = async () => {
    try { await api.post(`/team/${id}/deactivate`); toast.success("Zaměstnanec deaktivován."); setConfirm(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doReactivate = async () => {
    try { await api.post(`/team/${id}/reactivate`); toast.success("Zaměstnanec aktivován."); setConfirm(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteMember = async () => {
    try {
      await api.delete(`/team/${id}`, { data: { confirm: confirmName } });
      toast.success("Člen byl trvale smazán");
      setDelOpen(false);
      nav("/tym");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!m) return <div className="text-muted-foreground">Načítám…</div>;
  const s = m.stats || {};
  const companyMode = company?.attendance_mode_default === "daysheet" ? "daysheet" : "site";
  const companyTarget = company?.daily_target_hours != null ? company.daily_target_hours : 8;
  const companyPhotos = !!company?.checkout_photos_required;
  const isWorkerRole = m?.role === "WORKER" || m?.role === "SUBCONTRACTOR";
  const saveCheckoutPhotos = async (v) => {
    try { await api.patch(`/team/${id}`, { checkout_photos: v === "inherit" ? null : v }); toast.success("Uloženo."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const rateHistory = [...(m.rate_history || [])].sort((a, b) => (b.valid_from || "").localeCompare(a.valid_from || ""));
  const statusBadge = m.status === "inactive"
    ? <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-sm">neaktivní</span>
    : m.status === "invited"
    ? <span className="text-xs bg-amber-500/15 text-amber-500 px-2 py-0.5 rounded-sm">{m.invite_email_sent_at !== null ? "pozván" : "bez pozvánky"}</span>
    : <span className="text-xs bg-success/15 text-success px-2 py-0.5 rounded-sm">aktivní</span>;

  return (
    <div className="space-y-6" data-testid="member-detail-page">
      <button onClick={() => nav("/tym")} className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm"><ArrowLeft size={16} /> Zpět na tým</button>

      <div className="flex items-center gap-4 flex-wrap">
        <Avatar name={m.name} photo={m.photo_url} size={64} className="bg-secondary text-foreground" testid="member-avatar" />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-3xl font-black tracking-tighter uppercase truncate">{m.name}</h1>
            {statusBadge}
          </div>
          <p className="text-muted-foreground">{ROLE_LABELS[m.role]}{m.position ? ` · ${m.position}` : ""}</p>
        </div>
        {canDeactivate && (
          <div className="ml-auto">
            {m.status === "inactive"
              ? <Button onClick={() => setConfirm("reactivate")} data-testid="reactivate-btn" className="h-10 font-bold">Aktivovat</Button>
              : <Button variant="outline" onClick={() => setConfirm("deactivate")} data-testid="deactivate-btn" className="h-10 font-bold text-destructive border-destructive hover:bg-destructive/10">Deaktivovat</Button>}
          </div>
        )}
      </div>

      <div className={`grid grid-cols-2 ${canSeeRates && s.month_labor_cost != null ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-4`}>
        <StatCard icon={Clock} label="Hodiny tento měsíc" value={formatHours(s.month_hours)} testid="member-stat-hours" />
        {canSeeRates && s.month_labor_cost != null && <StatCard icon={Wallet} label="Mzdové náklady/měs" value={formatCZKCompact(s.month_labor_cost)} title={formatCZK(s.month_labor_cost)} testid="member-stat-cost" />}
        <StatCard icon={ClipboardList} label="Aktivní úkoly" value={s.active_tasks ?? 0} testid="member-stat-tasks" />
        <StatCard icon={AlertTriangle} label="Po termínu" value={s.overdue_tasks ?? 0} danger onClick={() => setTab("tasks")} testid="member-stat-overdue" />
      </div>

      <div className="min-w-0">
        <TabSwitcher value={tab} onChange={setTab} tabs={[
          { value: "overview", label: "Přehled", testid: "member-tab-overview" },
          { value: "attendance", label: "Docházka", testid: "member-tab-attendance" },
          { value: "tasks", label: "Úkoly", testid: "member-tab-tasks", count: s.overdue_tasks || null },
          { value: "docs", label: "Dokumenty", testid: "member-tab-docs", count: expiredCount || null },
          ...(canPay ? [{ value: "pay", label: "Mzdy a sazby", testid: "member-tab-pay" }] : []),
          ...(canSeeRates ? [{ value: "settings", label: "Nastavení", testid: "member-tab-settings" }] : []),
        ]} />
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4 items-start">
          <div className="space-y-4 min-w-0">
            {expiredCount > 0 && (
              <div className="bg-destructive/5 border border-destructive/30 rounded-[20px] p-4 space-y-2" data-testid="member-blocking">
                <div className="text-xs font-bold uppercase tracking-wide text-destructive">Blokuje práci</div>
                <p className="text-sm font-semibold">{plur(expiredCount, "dokument má", "dokumenty mají", "dokumentů má")} prošlou platnost.</p>
                <Button variant="outline" onClick={() => setTab("docs")} className="h-9 w-full sm:w-auto font-bold border-destructive text-destructive hover:bg-destructive/10">Otevřít dokumenty</Button>
              </div>
            )}
            <div className="bg-card border border-border rounded-[20px] p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="font-bold">Kontakty</h2>
                {(m.role !== "OWNER" || isOwner) && (
                  <Button variant="outline" size="sm" onClick={openEdit} data-testid="contact-edit-btn" className="h-9"><Pencil size={14} /> Upravit</Button>
                )}
              </div>
              <div className="grid grid-cols-1 gap-3 text-sm">
                <Field icon={Mail} label="E-mail" value={m.email} href={m.email ? `mailto:${m.email}` : null} />
                <Field icon={Phone} label="Telefon" value={m.phone} href={m.phone ? `tel:${m.phone}` : null} />
                <Field icon={Smartphone} label="Mobil" value={m.mobile} href={m.mobile ? `tel:${m.mobile}` : null} />
                <Field icon={MapPin} label="Adresa" value={m.address} />
                <Field icon={LifeBuoy} label="Nouzový kontakt" value={m.emergency_contact_name ? `${m.emergency_contact_name}${m.emergency_contact_phone ? ` · ${m.emergency_contact_phone}` : ""}` : null} />
              </div>
              {m.internal_note && (
                <div className="pt-2 border-t border-border">
                  <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1"><StickyNote size={12} /> Interní poznámka</div>
                  <p className="text-sm whitespace-pre-wrap">{m.internal_note}</p>
                </div>
              )}
            </div>
            <div className="bg-card border border-border rounded-[20px] p-5 space-y-2 text-sm" data-testid="member-facts">
              <h2 className="font-bold mb-1">Fakta</h2>
              <FactRow label="Hodiny tento měsíc" value={formatHours(s.month_hours)} />
              <FactRow label="Aktivní úkoly" value={s.active_tasks ?? 0} />
              <FactRow label="Režim docházky" value={`${m.attendance_mode ? MODE_LABELS[m.attendance_mode] : `Dle firmy (${MODE_LABELS[companyMode]})`}${m.daily_target_hours != null ? ` · ${fmtH(m.daily_target_hours)} h` : ""}`} />
              {isWorkerRole && <FactRow label="Fotky při odhlášení" value={m.checkout_photos === "required" ? "Povinné" : m.checkout_photos === "optional" ? "Nepovinné" : `Dle firmy (${companyPhotos ? "povinné" : "nepovinné"})`} />}
              <FactRow label="Člen od" value={formatDate(m.created_at)} />
              <FactRow label="Poslední přihlášení" value={m.last_login ? formatDateTime(m.last_login) : "nikdy"} />
              {s.current_shift && <div className="flex items-center gap-2 text-success font-semibold pt-1"><CircleDot size={14} className="animate-pulse" /> Právě na směně: {s.current_shift.project_name}</div>}
            </div>
            {covMember && (
              <div className="bg-card border border-border rounded-[20px] p-5 space-y-3" data-testid="member-projects-card">
                <div className="flex items-center justify-between gap-2">
                  <h2 className="font-bold flex items-center gap-2"><Briefcase size={16} /> Zakázky</h2>
                  <span className="text-sm text-muted-foreground tabular-nums" data-testid="member-projects-count">{covMember.project_count} z {activeProjCount} aktivních</span>
                </div>
                {onProjects.length > 0 ? (
                  <div className="space-y-1 max-h-56 overflow-auto -mx-1 px-1">
                    {onProjects.map((p) => (
                      <button key={p.id} onClick={() => nav(`/zakazky/${p.id}`)} data-testid={`member-project-${p.id}`}
                        className="w-full text-left text-sm rounded-[10px] px-2.5 py-1.5 hover:bg-secondary flex items-center gap-2">
                        {p.code && <span className="text-xs text-muted-foreground font-mono shrink-0">{p.code}</span>}
                        <span className="truncate">{p.name}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Zatím na žádné aktivní zakázce.</p>
                )}
                {missingProjCount > 0 && (
                  <Button variant="outline" onClick={addToAllProjects} data-testid="add-to-all-projects-btn" className="h-10 w-full sm:w-auto font-bold">
                    <Plus size={15} /> Přidat na všechny aktivní zakázky
                  </Button>
                )}
              </div>
            )}
          </div>
          <div className="bg-card border border-border rounded-[20px] p-5 min-w-0" data-testid="member-activity">
            <h2 className="font-bold mb-3">Co se dělo</h2>
            <div className="flex gap-1.5 flex-wrap mb-3">
              {[["all", "Vše"], ["shift", "Směny"], ["task", "Úkoly"], ...(canSeeRates ? [["account", "Účet"]] : []), ["other", "Ostatní"]].map(([v, l]) => (
                <button key={v} onClick={() => setActFilter(v)} data-testid={`activity-filter-${v}`}
                  className={`h-9 px-3.5 rounded-full text-sm font-semibold border transition-colors ${actFilter === v ? "bg-brand text-white border-brand" : "bg-secondary border-border text-muted-foreground hover:text-foreground"}`}>{l}</button>
              ))}
            </div>
            {(() => {
              const list = (activity || []).filter((a) => actFilter === "all" ? true : actFilter === "other" ? ["diary", "doc", "pay"].includes(a.kind) : a.kind === actFilter);
              if (activity === null) return <p className="text-sm text-muted-foreground">Načítám…</p>;
              if (!list.length) return <p className="text-sm text-muted-foreground">V tomhle filtru nic není.</p>;
              return <div className="space-y-2.5">{list.map((a, i) => (
                <div key={i} className="flex items-start gap-3" data-testid={`activity-item-${a.kind}`}>
                  <span className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${DOT[a.kind] || "bg-muted-foreground"}`} />
                  <div className="min-w-0"><div className="text-sm font-semibold">{a.title}</div><div className="text-xs text-muted-foreground">{a.meta}</div></div>
                </div>
              ))}</div>;
            })()}
          </div>
        </div>
      )}

      {tab === "attendance" && <AttendanceMonthlyView userId={id} />}

      {tab === "tasks" && <MemberTasks userId={id} nav={nav} />}

      {tab === "docs" && <MemberDocs userId={id} onCounts={({ expired }) => setExpiredCount(expired)} />}

      {tab === "pay" && canPay && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          {m.role === "SUBCONTRACTOR" ? (
            <div className="bg-card border border-border rounded-[20px] p-5 space-y-2" data-testid="sub-info-card">
              <h2 className="font-bold flex items-center gap-2"><Coins size={16} /> Odměňování</h2>
              <p className="text-sm text-muted-foreground">Subdodavatel je placený od úkolu. Docházka se nepropisuje do mzdových nákladů — náklady vznikají schválením jeho faktur.</p>
            </div>
          ) : (isOwner || m.role !== "OWNER") && (
            <div className="bg-card border border-border rounded-[20px] p-5 space-y-3" data-testid="rate-card">
              <div className="flex items-center justify-between">
                <h2 className="font-bold flex items-center gap-2"><Coins size={16} /> Sazba</h2>
                <Button variant="outline" size="sm" onClick={() => { setRateForm({ rate: m.hourly_rate ?? "", valid_from: todayISO(), contract: m.contract || "HPP" }); setRateOpen(true); }} data-testid="rate-change-btn" className="h-9"><Pencil size={14} /> Změnit sazbu</Button>
              </div>
              {canSeeRates && (
                <span className="inline-flex items-center h-7 px-2.5 rounded-full bg-secondary text-[13px] font-semibold" data-testid="rate-contract">{contractOf(m.contract).name}</span>
              )}
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-3xl font-extrabold tracking-tight tabular-nums">{m.hourly_rate != null ? `${formatCZK(m.hourly_rate)}/h` : "—"}</span>
                {canSeeRates && m.hourly_rate != null && <span className="text-sm text-muted-foreground">{contractOf(m.contract).rateLabel.toLowerCase()}</span>}
              </div>
              {canSeeRates && m.hourly_rate != null && <RateCost m={m} s={s} company={company} />}
              <p className="text-xs text-muted-foreground">Náklady se počítají sazbou a typem platným v den směny.</p>
              {rateHistory.length > 0 && (
                <div className="pt-2 border-t border-border" data-testid="rate-history">
                  <div className="text-xs font-bold uppercase tracking-wide text-muted-foreground mb-1">Historie sazeb</div>
                  <div className="space-y-1">{rateHistory.map((r, i) => (
                    <div key={i} className="text-sm text-muted-foreground">od {formatDate(r.valid_from)} — <b className="text-foreground">{formatCZK(r.rate)}/h</b>{r.contract ? ` · ${contractOf(r.contract).label}` : ""}{r.changed_by_name ? ` · změnil ${r.changed_by_name}` : ""}</div>
                  ))}</div>
                </div>
              )}
            </div>
          )}
          {s.labor_by_month && s.labor_by_month.some((x) => x.cost > 0) && (
            <div className="bg-card border border-border rounded-[20px] p-5 space-y-3" data-testid="labor-chart">
              <h2 className="font-bold flex items-center gap-2"><Wallet size={16} /> Mzdové náklady po měsících</h2>
              {(() => { const max = Math.max(...s.labor_by_month.map((x) => x.cost), 1); return (
                <div className="space-y-2">{s.labor_by_month.map((x) => (
                  <div key={x.month} className="flex items-center gap-2 text-sm">
                    <span className="w-16 shrink-0 text-muted-foreground tabular-nums">{x.month.slice(5)}/{x.month.slice(2, 4)}</span>
                    <div className="flex-1 h-5 bg-secondary rounded-full overflow-hidden"><div className="h-full bg-brand rounded-full" style={{ width: `${Math.round(x.cost / max * 100)}%` }} /></div>
                    <span className="w-20 shrink-0 text-right font-semibold tabular-nums" title={formatCZK(x.cost)}>{formatCZKCompact(x.cost)}</span>
                  </div>
                ))}</div>
              ); })()}
              <p className="text-xs text-muted-foreground">Počítáno z odvedených hodin a sazby platné v den směny.</p>
            </div>
          )}
        </div>
      )}

      {tab === "settings" && canSeeRates && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          {(isOwner || m.role !== "OWNER") && (
          <div className="bg-card border border-border rounded-[20px] p-5 space-y-3" data-testid="attendance-mode-card">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-bold flex items-center gap-2"><Clock size={16} /> Režim docházky</h2>
              <Button variant="outline" size="sm" onClick={openAtt} data-testid="att-mode-change-btn" className="h-9"><Pencil size={14} /> Změnit</Button>
            </div>
            <div className="text-sm"><span className="text-muted-foreground">Režim: </span><b data-testid="att-mode-value">{m.attendance_mode ? MODE_LABELS[m.attendance_mode] : `Dle firmy (${MODE_LABELS[companyMode]})`}</b></div>
            <div className="text-sm"><span className="text-muted-foreground">Denní úvazek: </span><b data-testid="att-target-value">{m.daily_target_hours != null ? `${fmtH(m.daily_target_hours)} h` : `Dle firmy (${fmtH(companyTarget)} h)`}</b></div>
            <div className="flex items-start justify-between gap-3 pt-3 border-t border-border" data-testid="member-never-block-row">
              <div className="min-w-0">
                <div className="text-sm font-semibold">Pustit do práce i s nedoplněným dnem</div>
                <p className="text-[13px] text-muted-foreground mt-0.5">{company?.unfinished_days_block === 0
                  ? "Firma teď pouští do práce všechny (Nastavení › Docházka)."
                  : "Pro toho, kdo si docházku vede jen pro sebe a na konci dne nic nedoplňuje — třeba stavbyvedoucí. Připomínka zůstane, přihlášení neblokuje."}</p>
              </div>
              <Switch checked={!!m.attendance_never_block} onCheckedChange={saveNeverBlock} data-testid="member-never-block" className="shrink-0 mt-0.5" />
            </div>
            {m.role !== "OWNER" && !m.attendance_never_block && (
              <Button variant="outline" className="h-10 w-full sm:w-auto" data-testid="unblock-btn"
                onClick={async () => { try { await api.post("/daysheet/unblock", { user_id: m.id }); toast.success("Přihlášení pro dnešek odblokováno."); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } }}>Odblokovat přihlášení na dnešek</Button>
            )}
          </div>
          )}
          {["WORKER", "MANAGER", "ASSISTANT", "WAREHOUSE"].includes(m.role) && <VacationCard m={m} onSaved={load} />}
          <div className="bg-card border border-border rounded-[20px] p-5 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <h2 className="font-bold flex items-center gap-2"><Package size={16} /> Hlásit spotřebu materiálu</h2>
                <p className="text-sm text-muted-foreground mt-0.5">Při odhlášení ze směny se zeptáme, kolik čeho spotřeboval. Vypni, když materiál řeší někdo jiný.</p>
              </div>
              <Switch checked={m.report_materials !== false} disabled={m.role === "SUBCONTRACTOR"} onCheckedChange={saveReportMaterials} data-testid="member-report-materials" />
            </div>
            {m.role === "SUBCONTRACTOR" && <p className="text-[13px] text-muted-foreground">Subdodavatel materiál nehlásí nikdy — má ho v režii.</p>}
          </div>
          {isWorkerRole && (
            <div className="bg-card border border-border rounded-[20px] p-5 space-y-3" data-testid="member-checkout-photos-card">
              <div className="min-w-0">
                <h2 className="font-bold flex items-center gap-2"><Camera size={16} /> Fotky při odhlášení</h2>
                <p className="text-sm text-muted-foreground mt-0.5">Musí při odhlášení přidat aspoň jednu fotku z práce? Přebíjí firemní nastavení.</p>
              </div>
              <Select value={m.checkout_photos || "inherit"} onValueChange={saveCheckoutPhotos}>
                <SelectTrigger className="h-11" data-testid="member-checkout-photos"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inherit" data-testid="member-checkout-photos-inherit">Dle firmy ({companyPhotos ? "povinné" : "nepovinné"})</SelectItem>
                  <SelectItem value="required">Povinné</SelectItem>
                  <SelectItem value="optional">Nepovinné</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {isOwner && (
            <div className="lg:col-span-2 bg-destructive/5 border border-destructive/30 rounded-[20px] p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <div>
                <div className="font-semibold text-destructive flex items-center gap-2"><ShieldAlert size={16} /> Smazat člena natrvalo</div>
                <p className="text-sm text-muted-foreground">Nevratně odstraní účet, osobní údaje a docházku. E-mail půjde znovu použít.</p>
              </div>
              <Button variant="destructive" onClick={() => { setConfirmName(""); setDelOpen(true); }} data-testid="delete-member-btn" className="h-11 w-full md:w-auto font-bold shrink-0">Smazat natrvalo</Button>
            </div>
          )}
        </div>
      )}

      {/* Edit contacts dialog — stejné sekce jako dialog pozvánky (FormSection) */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Upravit údaje</DialogTitle><DialogDescription className="sr-only">Úprava kontaktů zaměstnance</DialogDescription></DialogHeader>
          <div className="space-y-3.5">
            <FormSection icon={User} title="Osobní údaje">
              <Label className="text-[13px] text-muted-foreground">Jméno a příjmení</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 mt-1.5 bg-card" data-testid="edit-name" />
            </FormSection>

            <FormSection icon={Mail} title="Kontaktní údaje">
              {canEditEmail && (
                <div className="min-w-0 mb-3">
                  <Label className="text-[13px] text-muted-foreground">{m.status === "active" ? "Přihlašovací e-mail" : "E-mail"}</Label>
                  <Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="jmeno@firma.cz" className="h-11 mt-1.5 bg-card w-full min-w-0" data-testid="member-email" />
                  {m.status === "invited" ? (
                    <p className="text-[13px] text-muted-foreground mt-1.5 leading-snug">Po doplnění půjde členovi odeslat pozvánku.</p>
                  ) : emailChanged ? (
                    <div className="mt-2 rounded-[12px] border border-amber-500/50 bg-amber-500/[0.08] p-3 text-[13px] leading-snug" data-testid="member-email-warn">
                      Mění se <b>přihlašovací e-mail</b>. {m.name} se odhlásí a přihlásí se novým e-mailem — přijde mu kód.
                      Docházka, úkoly i historie zůstávají.
                    </div>
                  ) : (
                    <p className="text-[13px] text-muted-foreground mt-1.5 leading-snug">Tímto e-mailem se přihlašuje. Změňte ho, když ke starému ztratil přístup.</p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="min-w-0"><Label className="text-[13px] text-muted-foreground">Telefon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11 mt-1.5 bg-card" /></div>
                <div className="min-w-0"><Label className="text-[13px] text-muted-foreground">Mobil</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className="h-11 mt-1.5 bg-card" /></div>
              </div>
              <div className="mt-3"><Label className="text-[13px] text-muted-foreground">Adresa</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="h-11 mt-1.5 bg-card" /></div>
            </FormSection>

            <FormSection icon={Briefcase} title="Zařazení">
              <div className={`grid gap-3 grid-cols-1 ${canEditRole && ROLES_WITH_POSITION.includes(form.role) ? "sm:grid-cols-2" : ""}`}>
                {canEditRole && (
                  <div className="min-w-0">
                    <Label className="text-[13px] text-muted-foreground">Role</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, position: ROLES_WITH_POSITION.includes(v) ? form.position : "" })}>
                      <SelectTrigger className="h-11 mt-1.5 bg-card" data-testid="edit-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {isOwner && <SelectItem value="OWNER">Majitel</SelectItem>}
                        <SelectItem value="MANAGER">Stavbyvedoucí</SelectItem>
                        <SelectItem value="WORKER">Dělník</SelectItem>
                        <SelectItem value="SUBCONTRACTOR">Subdodavatel</SelectItem>
                        <SelectItem value="ASSISTANT">Asistentka</SelectItem>
                        <SelectItem value="ACCOUNTANT">Účetní</SelectItem>
                        <SelectItem value="WAREHOUSE">Skladník</SelectItem>
                        <SelectItem value="DESIGNER">Projektant</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {ROLES_WITH_POSITION.includes(form.role) && (
                  <div className="min-w-0">
                    <Label className="text-[13px] text-muted-foreground">Pozice</Label>
                    <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="h-11 mt-1.5 bg-card" data-testid="edit-position"
                      placeholder={form.role === "SUBCONTRACTOR" ? "např. Elektrikář" : "např. Zedník"} />
                  </div>
                )}
              </div>
              {canEditRole && form.role === "OWNER" && form.role !== m.role && (
                <p className="text-[13px] text-amber-600 dark:text-amber-400 mt-2.5 leading-snug" data-testid="edit-role-owner-warning">
                  {OWNER_WARN}
                </p>
              )}
              {canEditRole && form.role !== m.role && form.role !== "OWNER" && (
                <p className="text-[13px] text-amber-600 dark:text-amber-400 mt-2.5 leading-snug" data-testid="edit-role-warning">
                  Změna role upraví oprávnění po příštím přihlášení. Dosavadní docházka a náklady zůstanou beze změny
                  {form.role === "SUBCONTRACTOR"
                    ? "; nová docházka se nebude propisovat do mzdových nákladů (subdodavatel fakturuje)."
                    : m.role === "SUBCONTRACTOR" ? "; pro výpočet mzdových nákladů nezapomeňte nastavit sazbu." : "."}
                </p>
              )}
              {!canEditRole && !ROLES_WITH_POSITION.includes(form.role) && (
                <p className="text-[13px] text-muted-foreground leading-snug">Role: {ROLE_LABELS[form.role] || form.role}</p>
              )}
            </FormSection>

            <FormSection icon={LifeBuoy} title="Nouzový kontakt a poznámka">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="min-w-0"><Label className="text-[13px] text-muted-foreground">Nouzový kontakt — jméno</Label><Input value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} className="h-11 mt-1.5 bg-card" /></div>
                <div className="min-w-0"><Label className="text-[13px] text-muted-foreground">Nouzový kontakt — telefon</Label><Input value={form.emergency_contact_phone} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} className="h-11 mt-1.5 bg-card" /></div>
              </div>
              <div className="mt-3">
                <Label className="text-[13px] text-muted-foreground flex items-center gap-1"><StickyNote size={12} /> Interní poznámka (vidí pouze vedení)</Label>
                <Textarea value={form.internal_note} onChange={(e) => setForm({ ...form, internal_note: e.target.value })} className="mt-1.5 bg-card" rows={3} />
              </div>
            </FormSection>

            <Button onClick={saveEdit} disabled={!form.name?.trim()} className="w-full h-12 font-bold" data-testid="edit-save">Uložit</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Attendance mode dialog — OWNER only */}
      <Dialog open={attOpen} onOpenChange={setAttOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Režim docházky</DialogTitle><DialogDescription className="sr-only">Nastavení režimu docházky a denního úvazku pro pracovníka</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Režim docházky</Label>
              <Select value={attForm.mode} onValueChange={(v) => setAttForm({ ...attForm, mode: v })}>
                <SelectTrigger className="h-11 mt-1 w-full" data-testid="att-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="inherit" data-testid="att-mode-inherit">Dle firmy ({MODE_LABELS[companyMode]})</SelectItem>
                  <SelectItem value="site" data-testid="att-mode-site">Na stavbě</SelectItem>
                  <SelectItem value="daysheet" data-testid="att-mode-daysheet">Denní výkaz</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Denní úvazek (h)</Label>
              <Input type="number" min={0} step={0.5} inputMode="decimal" value={attForm.target}
                onChange={(e) => setAttForm({ ...attForm, target: e.target.value })}
                placeholder={`dle firmy (${fmtH(companyTarget)} h)`}
                className="h-11 mt-1 w-full min-w-0" data-testid="att-target-input" />
              <p className="text-xs text-muted-foreground mt-1">Prázdné = převzít z firemního nastavení.</p>
            </div>
            <p className="text-xs text-muted-foreground">Režim nelze změnit, pokud má pracovník rozpracovaný den — nejdřív ho musí odeslat.</p>
            <Button onClick={saveAtt} className="w-full h-11 font-bold" data-testid="att-mode-save">Uložit</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rate change dialog */}
      <Dialog open={rateOpen} onOpenChange={setRateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Změnit sazbu</DialogTitle><DialogDescription className="sr-only">Nastavení hodinové sazby s platností od data</DialogDescription></DialogHeader>
          <div className="space-y-3">
            {canSeeRates && (
              <div><Label>Typ spolupráce</Label>
                <ContractPicker value={rateForm.contract} rate={rateForm.rate} onChange={(v) => setRateForm({ ...rateForm, contract: v })} testid="rate-contract" className="mt-1" />
              </div>
            )}
            <div><Label>{canSeeRates ? contractOf(rateForm.contract).rateLabel : "Nová sazba"} ({currencySymbol()}/h)</Label><Input type="number" min={0} inputMode="decimal" value={rateForm.rate} onChange={(e) => setRateForm({ ...rateForm, rate: e.target.value })} className="h-11 mt-1 text-base md:text-sm" data-testid="rate-input" /></div>
            <div><Label>Platí od</Label><Input type="date" value={rateForm.valid_from} onChange={(e) => setRateForm({ ...rateForm, valid_from: e.target.value })} className="h-11 mt-1 w-full min-w-0" data-testid="rate-valid-from" /></div>
            <p className="text-xs text-muted-foreground">Směny od tohoto dne se počítají novou sazbou a typem, starší zůstanou, jak byly.</p>
            <Button onClick={() => guardRate(saveRate)} disabled={rateBusy} className="w-full h-11 font-bold" data-testid="rate-save">{rateBusy ? "Ukládám…" : "Uložit sazbu"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm deactivate/reactivate */}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{confirm === "deactivate" ? "Deaktivovat zaměstnance?" : "Znovu aktivovat?"}</DialogTitle><DialogDescription className="sr-only">Potvrzení akce</DialogDescription></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {confirm === "deactivate"
              ? `${m.name} se po deaktivaci nepřihlásí do aplikace. Historie docházky a úkolů zůstane zachována. Případná běžící směna bude ukončena.`
              : `${m.name} znovu získá přístup do aplikace.`}
          </p>
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
            <Button variant="outline" onClick={() => setConfirm(null)} className="h-11">Zrušit</Button>
            {confirm === "deactivate"
              ? <Button variant="destructive" onClick={doDeactivate} className="h-11 font-bold" data-testid="confirm-deactivate">Deaktivovat</Button>
              : <Button onClick={doReactivate} className="h-11 font-bold" data-testid="confirm-reactivate">Aktivovat</Button>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Permanent delete dialog */}
      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-destructive">Trvale smazat člena {m.name}?</DialogTitle><DialogDescription className="sr-only">Nevratné smazání zaměstnance a jeho osobních dat</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
              <li>účet, kontakty, sazby a osobní údaje se smažou <b className="text-foreground">nevratně</b></li>
              <li>smaže se celá docházka → <b className="text-foreground">zpětně se sníží mzdové náklady v ekonomice zakázek</b></li>
              <li>úkoly přiřazené <b className="text-foreground">pouze jemu</b> se smažou; ze společných úkolů bude jen odebrán</li>
              <li>e-mail půjde znovu použít pro novou pozvánku</li>
              <li>deník a dokumenty zůstávají (se jménem autora)</li>
            </ul>
            <div>
              <Label>Pro potvrzení napište jméno člena</Label>
              <Input value={confirmName} onChange={(e) => setConfirmName(e.target.value)} placeholder={m.name} className="h-11 mt-1 w-full min-w-0" data-testid="delete-confirm-input" />
            </div>
            <div className="flex flex-col-reverse md:flex-row gap-2 md:justify-end">
              <Button variant="outline" onClick={() => setDelOpen(false)} className="h-11 w-full md:w-auto">Zrušit</Button>
              <Button variant="destructive" onClick={deleteMember} disabled={confirmName !== m.name} className="h-11 w-full md:w-auto font-bold" data-testid="confirm-delete-member">Smazat natrvalo</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FactRow({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold text-right">{value}</span>
    </div>
  );
}

function Field({ icon: Icon, label, value, href }) {
  return (
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground uppercase tracking-wide flex items-center gap-1">{Icon && <Icon size={12} />} {label}</div>
      {value ? (href
        ? <a href={href} className="text-brand hover:underline truncate block">{value}</a>
        : <div className="truncate">{value}</div>)
        : <div className="text-muted-foreground italic">—</div>}
    </div>
  );
}

function MemberTasks({ userId, nav }) {
  const [tasks, setTasks] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  // 218: jen úkoly tohoto člověka (celá jeho historie), ne všechny úkoly firmy
  useEffect(() => { api.get(`/tasks?assignee=${encodeURIComponent(userId)}`).then((r) => setTasks((r.data || []).filter((t) => t.assignees?.includes(userId)))).catch(() => {}); }, [userId]);
  const today = todayISO();
  const overdue = useMemo(() => tasks.filter((t) => (t.deadline || "").slice(0, 10) && (t.deadline || "").slice(0, 10) < today && !DONE_ST.includes(t.status)).sort((a, b) => (a.deadline || "").localeCompare(b.deadline || "")), [tasks, today]);
  const active = useMemo(() => tasks.filter((t) => ["Nový", "V řešení"].includes(t.status) && !overdue.includes(t)).sort((a, b) => (a.deadline || "z").localeCompare(b.deadline || "z")), [tasks, overdue]);
  const history = useMemo(() => tasks.filter((t) => DONE_ST.includes(t.status)).sort((a, b) => (b.completion?.at || "").localeCompare(a.completion?.at || "")), [tasks]);

  const Row = (t) => (
    <button key={t.id} onClick={() => nav(`/ukoly/${t.id}`)} data-testid={`member-task-row-${t.id}`}
      className="w-full text-left bg-card border border-border rounded-[14px] p-3 hover:bg-secondary/60 transition-colors">
      <div className="font-semibold truncate">{t.title}</div>
      <div className="text-sm text-muted-foreground flex items-center gap-2 flex-wrap">
        <span>{t.project_name || "—"}</span>
        {t.deadline && <span className={(t.deadline || "").slice(0, 10) < today && !DONE_ST.includes(t.status) ? "text-destructive" : ""}>· {formatDate(t.deadline)}</span>}
        <span>· {t.priority}</span>
        <span>· {STATUS_LABEL[t.status] || t.status}</span>
        {DONE_ST.includes(t.status) && t.completion?.at && <span className="text-success">· hotovo {formatDate(t.completion.at)}</span>}
      </div>
    </button>
  );

  return (
    <div className="space-y-5">
      {overdue.length > 0 && (
        <div className="space-y-2">
          <h3 className="font-bold text-destructive flex items-center gap-2"><AlertTriangle size={16} /> Po termínu ({overdue.length})</h3>
          {overdue.map(Row)}
        </div>
      )}
      <div className="space-y-2">
        <h3 className="font-bold">Aktivní ({active.length})</h3>
        {active.length ? active.map(Row) : <p className="text-sm text-muted-foreground">Žádné aktivní úkoly.</p>}
      </div>
      {history.length > 0 && (
        <div className="space-y-2">
          <button onClick={() => setShowHistory((s) => !s)} className="flex items-center gap-2 font-bold text-muted-foreground hover:text-foreground">
            {showHistory ? <ChevronDown size={16} /> : <ChevronRight size={16} />} Historie ({history.length})
          </button>
          {showHistory && history.map(Row)}
        </div>
      )}
    </div>
  );
}


const BADGE = {
  valid: { cls: "bg-success/15 text-success", label: "platné" },
  expiring: { cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400", label: "vyprší brzy" },
  expired: { cls: "bg-destructive/15 text-destructive", label: "prošlé" },
};

function daysLeft(vu) {
  const diff = Math.round((new Date(vu + "T00:00:00") - new Date(todayISO() + "T00:00:00")) / 86400000);
  return diff;
}

function MemberDocs({ userId, onCounts }) {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ doc_type: "Školení BOZP", name: "", valid_until: "", note: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [del, setDel] = useState(null);

  const load = useCallback(() => api.get(`/team/${userId}/documents`).then((r) => {
    setDocs(r.data);
    onCounts?.({ expired: (r.data || []).filter((d) => d.expiry_status === "expired").length });
  }).catch(() => {}), [userId, onCounts]);
  useEffect(() => { load(); }, [load]);

  const reset = () => { setForm({ doc_type: "Školení BOZP", name: "", valid_until: "", note: "" }); setFile(null); };

  const save = async () => {
    if (!file) return toast.error("Vyberte soubor.");
    setSaving(true);
    try {
      const file_url = await uploadFile(file);
      await api.post(`/team/${userId}/documents`, {
        doc_type: form.doc_type, name: form.name || null, file_url,
        valid_until: form.valid_until || null, note: form.note || null,
      });
      toast.success("Dokument přidán."); setOpen(false); reset(); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Nahrání selhalo."); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/team/${userId}/documents/${del.id}`); toast.success("Dokument smazán."); setDel(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="bg-card border border-border rounded-[20px] p-5 space-y-4" data-testid="member-docs-card">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-bold flex items-center gap-2"><FileText size={16} /> Dokumenty a certifikáty</h2>
        <Button size="sm" onClick={() => { reset(); setOpen(true); }} data-testid="doc-add-btn" className="h-9 font-bold"><Plus size={15} /> Přidat dokument</Button>
      </div>

      {docs.length === 0 ? (
        <p className="text-sm text-muted-foreground">Zatím žádné dokumenty. Přidejte školení, průkazy nebo lékařské prohlídky a hlídejte jejich platnost.</p>
      ) : (
        <div className="space-y-2">
          {docs.map((d) => {
            const b = BADGE[d.expiry_status] || BADGE.valid;
            const dl = d.valid_until ? daysLeft(d.valid_until) : null;
            const badgeLabel = d.expiry_status === "expiring" && dl != null ? `vyprší za ${dl} dní`
              : d.expiry_status === "expired" ? "prošlé" : "platné";
            return (
              <div key={d.id} data-testid={`doc-row-${d.id}`}
                className="flex items-center gap-3 p-3 rounded-[14px] border border-border hover:bg-secondary/50 transition-colors">
                <button onClick={() => downloadSigned(d.file_url, d.name || d.doc_type)} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-testid={`doc-open-${d.id}`}>
                  <FileText size={18} className="text-muted-foreground shrink-0" />
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{d.doc_type}{d.name ? ` · ${d.name}` : ""}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {d.valid_until ? `platnost do ${formatDate(d.valid_until)}` : "bez expirace"}
                      {d.uploaded_by_name ? ` · nahrál ${d.uploaded_by_name}` : ""}
                    </div>
                  </div>
                </button>
                {d.valid_until && (
                  <span data-testid={`doc-badge-${d.id}`} className={`text-xs px-2 py-1 rounded-sm font-semibold shrink-0 inline-flex items-center gap-1 ${b.cls}`}>
                    {d.expiry_status !== "valid" && <AlertTriangle size={12} />} {badgeLabel}
                  </span>
                )}
                <Button variant="ghost" size="icon" onClick={() => setDel(d)} data-testid={`doc-delete-${d.id}`} className="shrink-0 text-muted-foreground hover:text-destructive"><Trash2 size={16} /></Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Add dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Přidat dokument</DialogTitle><DialogDescription className="sr-only">Nahrání dokumentu zaměstnance</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Typ dokumentu</Label>
              <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                <SelectTrigger className="h-11 mt-1" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Název (nepovinné)</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 mt-1" placeholder="např. BOZP 2026" /></div>
            <div>
              <Label>Soubor</Label>
              <label className="mt-1 flex items-center gap-2 h-11 px-3 rounded-[14px] border border-dashed border-border cursor-pointer hover:border-brand transition-colors" data-testid="doc-file-label">
                <Upload size={16} className="text-muted-foreground" />
                <span className="text-sm truncate">{file ? file.name : "Vybrat soubor (foto nebo PDF)"}</span>
                <input type="file" accept="image/*,application/pdf" className="hidden" data-testid="doc-file-input"
                  onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </label>
            </div>
            <div><Label>Platnost do (nepovinné)</Label><Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} className="h-11 mt-1 w-full min-w-0" data-testid="doc-valid-until" /></div>
            <div><Label>Poznámka (nepovinné)</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="h-11 mt-1" /></div>
            <Button onClick={save} disabled={saving} className="w-full h-11 font-bold" data-testid="doc-save">
              {saving ? "Nahrávám…" : <><CheckCircle2 size={16} /> Uložit dokument</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Smazat dokument?</DialogTitle><DialogDescription className="sr-only">Potvrzení smazání</DialogDescription></DialogHeader>
          <p className="text-sm text-muted-foreground">Opravdu smazat „{del?.doc_type}{del?.name ? ` · ${del.name}` : ""}"? Tuto akci nelze vrátit.</p>
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
            <Button variant="outline" onClick={() => setDel(null)} className="h-11">Zrušit</Button>
            <Button variant="destructive" onClick={remove} className="h-11 font-bold" data-testid="doc-confirm-delete">Smazat</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// Nárok na dovolenou (176) — vybrané dny se počítají ze schválené nepřítomnosti.
function VacationCard({ m, onSaved }) {
  const [bal, setBal] = useState(null);
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState("");
  const loadBal = useCallback(() => {
    api.get(`/absences/balance?user_id=${m.id}`).then((r) => setBal(r.data)).catch(() => setBal(null));
  }, [m.id]);
  useEffect(() => { loadBal(); }, [loadBal, m.vacation_days]);
  const fmtD = (n) => String(n ?? 0).replace(".", ",");
  const save = async () => {
    const t = val.trim().replace(",", ".");
    const n = t === "" ? null : Number(t);
    if (n != null && (isNaN(n) || n < 0 || n > 60)) return toast.error("Zadejte 0–60 dní.");
    try {
      await api.patch(`/team/${m.id}`, { vacation_days: n });
      toast.success("Nárok na dovolenou uložen."); setEdit(false); onSaved?.(); loadBal();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-card border border-border rounded-[20px] p-5 space-y-3" data-testid="vacation-card">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-bold flex items-center gap-2"><Clock size={16} /> Dovolená</h2>
        {!edit && <Button variant="outline" size="sm" onClick={() => { setVal(m.vacation_days != null ? String(m.vacation_days) : ""); setEdit(true); }} data-testid="vacation-edit" className="h-9"><Pencil size={14} /> Změnit</Button>}
      </div>
      {edit ? (
        <div className="space-y-2">
          <Label htmlFor="vac-days">Nárok za rok (dní)</Label>
          <Input id="vac-days" inputMode="decimal" value={val} onChange={(e) => setVal(e.target.value)} placeholder="např. 20" className="h-11 w-full min-w-0" data-testid="vacation-input" />
          <p className="text-xs text-muted-foreground">Nechte prázdné, když dovolenou u tohoto člověka neevidujete.</p>
          <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2">
            <Button variant="outline" onClick={() => setEdit(false)} className="h-11 w-full md:w-auto">Zrušit</Button>
            <Button onClick={save} className="h-11 w-full md:w-auto font-bold" data-testid="vacation-save">Uložit</Button>
          </div>
        </div>
      ) : (
        <div className="text-sm space-y-1">
          <div><span className="text-muted-foreground">Nárok: </span><b data-testid="vacation-entitlement">{m.vacation_days != null ? `${fmtD(m.vacation_days)} dní / rok` : "neevidováno"}</b></div>
          {bal && <div><span className="text-muted-foreground">Letos vybráno: </span><b className="tabular-nums">{fmtD(bal.used)} dní</b>{bal.pending ? <span className="text-muted-foreground"> · čeká {fmtD(bal.pending)}</span> : null}</div>}
          {bal?.remaining != null && <div><span className="text-muted-foreground">Zbývá: </span><b className="tabular-nums" data-testid="vacation-remaining">{fmtD(bal.remaining)} dní</b></div>}
        </div>
      )}
    </div>
  );
}

// 281: kolik člověk firmu stojí podle typu spolupráce (a u DPP, kolik zbývá do limitu).
function RateCost({ m, s, company }) {
  const t = laborTerms(company);
  const rate = m.hourly_rate || 0;
  if (m.contract === "ICO") return <p className="text-sm text-muted-foreground" data-testid="rate-cost">Odvody a daně si platí sám — firmu stojí <b className="text-foreground tabular-nums">{formatCZK(rate)}/h</b>.</p>;
  if (m.contract === "DPP") {
    const d = s.dpp || { limit: t.limit, earned: 0 };
    const over = d.earned >= d.limit;
    const pct = Math.min(100, Math.round((d.earned / Math.max(d.limit, 1)) * 100));
    return (
      <div className="space-y-1.5" data-testid="rate-cost">
        <div className="flex items-baseline justify-between gap-2 text-sm">
          <span className="text-muted-foreground">Tento měsíc</span>
          <span className="tabular-nums whitespace-nowrap"><b>{formatCZK(d.earned)}</b> <span className="text-muted-foreground">z {formatCZK(d.limit)}</span></span>
        </div>
        <div className="h-2 rounded-full bg-secondary overflow-hidden">
          <div className={`h-full rounded-full ${over ? "bg-amber-500" : "bg-brand"}`} style={{ width: `${pct}%` }} />
        </div>
        <p className={`text-sm ${over ? "text-amber-600 dark:text-amber-400 font-semibold" : "text-muted-foreground"}`}>
          {over ? `Nad limitem — firma za tento měsíc platí odvody z celé částky (${formatCZK(rate * t.coef)}/h).`
                : `Pod limitem bez odvodů — firmu stojí ${formatCZK(rate)}/h.`}
        </p>
      </div>
    );
  }
  return <p className="text-sm text-muted-foreground" data-testid="rate-cost">S odvody {t.pctText} % firmu stojí <b className="text-foreground tabular-nums">{formatCZK(rate * t.coef)}/h</b>. Na ruku dostane méně — strhne se mu pojištění a daň.</p>;
}
